import { useState } from 'react';
import { Box, IconButton, Skeleton, Tooltip, Typography, useTheme } from '@mui/material';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';

import { AppCard, FilterBar } from '../../shared/components/ui';
import { CategoriaCotacao, ICotacao, ICotacoesMercado } from '../../shared/services/api/mercado';
import { formatarValorBRL, formatarValorCotacao, formatarVariacao, sufixoUnidade } from './formatacao';
import { SecaoGrade } from './SecaoGrade';
import { Sparkline } from './Sparkline';

type FiltroCotacao = 'destaques' | 'moeda' | 'commodity' | 'combustivel' | 'indices';

const FILTROS: { value: FiltroCotacao; label: string }[] = [
  { value: 'destaques', label: 'Destaques' },
  { value: 'moeda', label: 'Moedas' },
  { value: 'commodity', label: 'Commodities' },
  { value: 'combustivel', label: 'Combustíveis' },
  { value: 'indices', label: 'Índices' },
];

// O que aparece em "Destaques", nessa ordem — o que mais pesa no dia a dia
// de quem compra, vende e transporta mercadoria.
const DESTAQUES = ['USD', 'EUR', 'BRENT', 'FERRO', 'GASOLINA', 'DIESELS10', 'IBOV', 'SELIC', 'XAU', 'SOJA', 'MILHO', 'CAFE'];

const CATEGORIAS_DO_FILTRO: Record<Exclude<FiltroCotacao, 'destaques'>, CategoriaCotacao[]> = {
  moeda: ['moeda'],
  commodity: ['commodity'],
  combustivel: ['combustivel'],
  indices: ['indice', 'indicador'],
};

const filtrarCotacoes = (itens: ICotacao[], filtro: FiltroCotacao) => {
  if (filtro === 'destaques') {
    return DESTAQUES.map(codigo => itens.find(item => item.codigo === codigo)).filter((item): item is ICotacao => !!item);
  }
  return itens.filter(item => CATEGORIAS_DO_FILTRO[filtro].includes(item.categoria));
};

const LARGURA_MINIMA_CARD = 168;

const formatarNumero = (valor: number) => valor.toLocaleString('pt-BR', { maximumFractionDigits: 2 });

// Linha abaixo do valor: o equivalente em reais quando a cotação é em dólar,
// a semana do levantamento nos combustíveis, ou vazia (mantém a altura).
const linhaComplementar = (cotacao: ICotacao) => {
  const valorBRL = formatarValorBRL(cotacao);
  if (valorBRL) return valorBRL;
  if (cotacao.categoria === 'combustivel' && cotacao.referencia) return `Semana ${cotacao.referencia}`;
  return ' '; // espaço não separável: mantém a altura da linha
};

const CardCotacao: React.FC<{ cotacao: ICotacao }> = ({ cotacao }) => {
  const theme = useTheme();
  const variacao = formatarVariacao(cotacao);
  const subiu = (cotacao.variacao ?? 0) > 0;
  const caiu = (cotacao.variacao ?? 0) < 0;
  const corVariacao = subiu ? theme.palette.success.main : caiu ? theme.palette.error.main : theme.palette.text.secondary;
  const { historico } = cotacao;
  const tendenciaAlta = historico.length > 1 && historico[historico.length - 1] >= historico[0];
  const sufixo = sufixoUnidade(cotacao);

  const detalhe = [
    `Fonte: ${cotacao.fonte}`,
    cotacao.referencia && (cotacao.categoria === 'combustivel' ? `Semana de ${cotacao.referencia} (média Brasil)` : `Referência: ${cotacao.referencia}`),
    historico.length > 1 && `Mínima/máxima no período: ${formatarNumero(Math.min(...historico))} / ${formatarNumero(Math.max(...historico))}`,
  ].filter(Boolean).join(' · ');

  // Box em volta do AppCard: o Tooltip precisa de um filho que aceite ref, e
  // o AppCard não repassa.
  return (
    <Tooltip title={detalhe} placement="top" arrow>
      <Box sx={{ height: '100%' }}>
        <AppCard sx={{ height: '100%', boxSizing: 'border-box', p: 1.5, '@media (max-height:850px)': { p: 1 }, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" noWrap>{cotacao.nome}</Typography>
            {variacao && (
              <Box display="flex" alignItems="center" sx={{ color: corVariacao, flexShrink: 0 }}>
                {subiu && <ArrowDropUpRoundedIcon sx={{ fontSize: 18, mx: -0.5 }} />}
                {caiu && <ArrowDropDownRoundedIcon sx={{ fontSize: 18, mx: -0.5 }} />}
                <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11 }}>{variacao.replace(/^[+-]/, '')}</Typography>
              </Box>
            )}
          </Box>
          <Box display="flex" alignItems="baseline" gap={0.5} minWidth={0}>
            <Typography sx={{ fontSize: 18, fontWeight: 750, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
              {formatarValorCotacao(cotacao)}
            </Typography>
            {sufixo && <Typography variant="caption" color="text.secondary" noWrap>{sufixo}</Typography>}
          </Box>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: 11 }}>
            {linhaComplementar(cotacao)}
          </Typography>
          <Sparkline valores={historico} cor={tendenciaAlta ? theme.palette.success.main : theme.palette.error.main} />
        </AppCard>
      </Box>
    </Tooltip>
  );
};

interface IPainelCotacoesProps {
  dados: ICotacoesMercado | null;
  carregando: boolean;
  erro: string | null;
  onAtualizar: () => void;
}

export const PainelCotacoes: React.FC<IPainelCotacoesProps> = ({ dados, carregando, erro, onAtualizar }) => {
  const [filtro, setFiltro] = useState<FiltroCotacao>('destaques');
  const cotacoes = dados ? filtrarCotacoes(dados.itens, filtro) : [];
  const horario = dados ? new Date(dados.atualizadoEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : null;

  return (
    <Box>
      <SecaoGrade
        // key: trocar o filtro volta pra primeira página da linha.
        key={filtro}
        titulo="Cotações do mercado"
        subtitulo={horario ? `atualizado às ${horario}` : undefined}
        icone={<ShowChartRoundedIcon fontSize="small" />}
        larguraMinimaCard={LARGURA_MINIMA_CARD}
        acoes={(
          <>
            <FilterBar chips={FILTROS} value={filtro} onChange={setFiltro} />
            <Tooltip title="Atualizar cotações">
              <span>
                <IconButton size="small" onClick={onAtualizar} disabled={carregando}>
                  <RefreshRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      >
        {carregando && !dados && Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={122} />
        ))}
        {!carregando && !dados && (
          <Typography variant="body2" color="text.secondary" py={2} sx={{ gridColumn: '1 / -1' }}>
            {erro || 'Cotações indisponíveis no momento.'}
          </Typography>
        )}
        {cotacoes.map(cotacao => <CardCotacao key={cotacao.codigo} cotacao={cotacao} />)}
      </SecaoGrade>
      <Typography variant="caption" color="text.secondary" display="block" mt={0.75} sx={{ fontSize: 11 }}>
        Valores de referência, com possível atraso. Fontes: AwesomeAPI (câmbio), Yahoo Finance (commodities e Ibovespa),
        Banco Central (Selic, CDI, IPCA) e ANP (preço médio semanal dos combustíveis no Brasil).
      </Typography>
    </Box>
  );
};
